import { motion } from 'framer-motion';
import { useState } from 'react';
import { FormStepProps } from '../../../types/applicationTypes';

export default function Step2ParentInfo({ data, errors, updateData }: FormStepProps) {
  const [activeTab, setActiveTab] = useState<'parent1' | 'parent2'>('parent1');
  const parentErrors = errors?.parent ?? {};
  const hasParentErrors = Object.values(parentErrors).some((value) => Boolean(value));

  const inputClass = (hasError: boolean) =>
    `w-full px-4 py-3 border rounded-lg transition-colors ${
      hasError
        ? 'border-red-500 focus:ring-2 focus:ring-red-300 focus:border-red-500'
        : 'border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-blue-300'
    }`;

  const handleInputChange = (
    parentKey: 'parent' | 'parent2',
    field: keyof typeof data.parent,
    value: string
  ) => {
    updateData({
      [parentKey]: {
        ...data[parentKey],
        [field]: value
      }
    });
  };

  const renderParentForm = (parentKey: 'parent' | 'parent2', title: string, isRequired: boolean) => {
    const fieldErrors = parentKey === 'parent' ? parentErrors : (errors?.parent2 ?? {});

    return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            First Name{isRequired && '*'}
          </label>
          <input
            type="text"
            value={data[parentKey].parentFirstName}
            onChange={(e) => handleInputChange(parentKey, 'parentFirstName', e.target.value)}
            placeholder="Enter First Name"
            className={inputClass(Boolean(fieldErrors.parentFirstName))}
            required={isRequired}
            aria-invalid={Boolean(fieldErrors.parentFirstName)}
          />
          {fieldErrors.parentFirstName && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.parentFirstName}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Last Name{isRequired && '*'}
          </label>
          <input
            type="text"
            value={data[parentKey].parentLastName}
            onChange={(e) => handleInputChange(parentKey, 'parentLastName', e.target.value)}
            placeholder="Enter Last Name"
            className={inputClass(Boolean(fieldErrors.parentLastName))}
            required={isRequired}
            aria-invalid={Boolean(fieldErrors.parentLastName)}
          />
          {fieldErrors.parentLastName && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.parentLastName}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email{isRequired && '*'}
          </label>
          <input
            type="email"
            value={data[parentKey].parentEmail}
            onChange={(e) => handleInputChange(parentKey, 'parentEmail', e.target.value)}
            placeholder="Enter Email"
            className={inputClass(Boolean(fieldErrors.parentEmail))}
            required={isRequired}
            aria-invalid={Boolean(fieldErrors.parentEmail)}
          />
          {fieldErrors.parentEmail && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.parentEmail}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone{isRequired && '*'}
          </label>
          <input
            type="tel"
            value={data[parentKey].parentPhone}
            onChange={(e) => handleInputChange(parentKey, 'parentPhone', e.target.value)}
            placeholder="Enter Phone"
            className={inputClass(Boolean(fieldErrors.parentPhone))}
            required={isRequired}
            aria-invalid={Boolean(fieldErrors.parentPhone)}
          />
          {fieldErrors.parentPhone && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.parentPhone}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Work Address
          </label>
          <input
            type="text"
            value={data[parentKey].workAddress}
            onChange={(e) => handleInputChange(parentKey, 'workAddress', e.target.value)}
            placeholder="Enter Work Address"
            className={inputClass(false)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Work Phone
          </label>
          <input
            type="tel"
            value={data[parentKey].workPhone}
            onChange={(e) => handleInputChange(parentKey, 'workPhone', e.target.value)}
            placeholder="Enter Work Phone"
            className={inputClass(false)}
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Relationship{isRequired && '*'}
        </label>
        <select
          value={data[parentKey].relationship}
          onChange={(e) => handleInputChange(parentKey, 'relationship', e.target.value)}
          className={inputClass(Boolean(fieldErrors.relationship))}
          required={isRequired}
          aria-invalid={Boolean(fieldErrors.relationship)}
        >
          <option value="">Select Relationship</option>
          <option value="mother">Mother</option>
          <option value="father">Father</option>
          <option value="guardian">Guardian</option>
        </select>
        {fieldErrors.relationship && (
          <p className="mt-1 text-sm text-red-600">{fieldErrors.relationship}</p>
        )}
      </div>
    </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center mb-6">
        <div className="bg-blue-100 p-3 rounded-full mr-4">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-blue-600">
          Parent/Guardian Information
        </h3>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('parent1')}
          className={`px-6 py-3 font-medium text-sm transition-colors relative ${
            activeTab === 'parent1'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Parent/Guardian 1*
          {activeTab === 'parent1' && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
              layoutId="activeTab"
            />
          )}
        </button>
        <button
          onClick={() => setActiveTab('parent2')}
          className={`px-6 py-3 font-medium text-sm transition-colors relative ${
            activeTab === 'parent2'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Parent/Guardian 2 (Optional)
          {activeTab === 'parent2' && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
              layoutId="activeTab"
            />
          )}
        </button>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'parent1' && renderParentForm('parent', 'Parent/Guardian 1', true)}
        {activeTab === 'parent2' && (
          <>
            {hasParentErrors && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-700 text-sm">
                  Parent/Guardian 1 is required. Please complete the required fields before continuing.
                </p>
              </div>
            )}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-amber-800 text-sm">
                <strong>Optional:</strong> Add a second parent/guardian if applicable. All fields in this section are optional.
              </p>
            </div>
            {renderParentForm('parent2', 'Parent/Guardian 2', false)}
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
